'use client';

import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { When } from 'react-if';
import { v4 as uuidv4 } from 'uuid';

import { priceFormatter } from '@/lib/helper';
import { useClientSmallScreen } from '@/lib/user-agent/client';

import Tooltip from '@/components/@base/tooltip';

import {
  DATA_CY_CART_BUTTON,
  DATA_CY_STEPPED_DISCOUNT_SECTION,
  DATA_CY_STEPPED_DISCOUNT_TOOLTIP,
  DATA_CY_STEPPED_DISCOUNT_TOOLTIP_TEXT,
  DATA_CY_STEPS,
} from '@/constant/cypress';
import localization from '@/constant/localization';

const staticPositions = [30, 60, 90];

type Props = {
  data: {
    currentAmount: number;
    steps: {};
    neededAmount: number;
    needToNextStep: number;
  };
  percentClassName?: string;
  amountClassName?: string;
  containerClassName?: string;
  isModal?: boolean;
};

export default function SteppedDiscount({
  data,
  percentClassName,
  amountClassName,
  containerClassName,
  isModal,
}: Props) {
  const isSmallScreen = useClientSmallScreen();

  const tooltipId = uuidv4();
  const [progress, setProgress] = useState(data.currentAmount);
  useEffect(() => {
    setProgress(data?.currentAmount);
  }, [data?.currentAmount]);
  const orderedKeys = ['one', 'two', 'three'];
  const discounts = orderedKeys.map((key) => data?.steps[key]);

  const getProgressPercent = () => {
    const firstStep = discounts[0];
    const lastStep = discounts[discounts.length - 1];

    if (progress < firstStep.neededAmount) {
      const percent = (progress / firstStep.neededAmount) * staticPositions[0];
      return Math.max(0, percent);
    }

    for (let i = 0; i < discounts.length - 1; i++) {
      const current = discounts[i];
      const next = discounts[i + 1];

      if (progress < next.neededAmount) {
        const range = next.neededAmount - current.neededAmount;
        const passed = progress - current.neededAmount;
        const progressInRange = passed / range;

        const startPercent = staticPositions[i];
        const endPercent = staticPositions[i + 1];
        return startPercent + (endPercent - startPercent) * progressInRange;
      }
    }

    if (progress >= lastStep.neededAmount) {
      return 100;
    }

    return 0;
  };

  const progressPercent = getProgressPercent();
  const adjustProgressPercent = (
    progressPercent: number,
    progress: number,
    discounts: any[],
  ) => {
    const threshold = 1.5;
    const offset = isSmallScreen ? 4.75 : 3.85;
    const offset1 = isSmallScreen ? 3.25 : 2.5;
    for (let i = 0; i < staticPositions.length; i++) {
      const circlePos = staticPositions[i];
      const step = discounts[i];
      if (!step) continue;
      const isNear = Math.abs(progressPercent - circlePos) < threshold;
      if (isNear) {
        if (progress < step.neededAmount) {
          return progressPercent - offset;
        } else if (progress > step.neededAmount) {
          return progressPercent + offset1;
        }
      }
    }
    return progressPercent;
  };

  return (
    <div
      data-cy={DATA_CY_STEPPED_DISCOUNT_SECTION}
      dir='ltr'
      className={clsx(
        containerClassName,
        'bg-withe! relative mx-auto -mt-3 w-full max-w-2xl px-4',
      )}
    >
      {!isModal && <div className='-z-10! ml-1.5 h-[1px] w-[99%] bg-[#E5E8EB]' />}
      <div
        className={clsx(
          percentClassName,
          'mt-6 mr-2 ml-[88px] flex justify-between pt-5 text-[11px] text-[#76767C]',
        )}
      >
        {discounts.map((item, idx) => (
          <div
            key={idx}
            className='flex-reverce mx-1 mb-1 flex items-center gap-0.5 text-center'
          >
            <span> {localization.discount} %</span>
            <span className='leading-[24px] font-light'>{item.discountPercent}</span>
          </div>
        ))}
      </div>
      <div className='relative h-2 rounded-full bg-gray-200'>
        <div
          className='absolute left-0 h-2 rounded-full bg-orange-400 shadow-[10px_6px_15px_-3px_#FFAB22] transition-all'
          style={{
            width: `${adjustProgressPercent(progressPercent, progress, discounts) + 1}%`,
          }}
        />

        {staticPositions.map((percent, idx) => {
          const isReached = progress >= discounts[idx]?.neededAmount;
          return (
            <div
              key={idx}
              className='absolute top-1/2 z-30! -translate-x-1/2 -translate-y-1/2'
              style={{ left: `${percent}%` }}
            >
              <div
                className={clsx(
                  'h-[18px] w-[18px] rounded-full border-[3px] border-white shadow-[6px_6px_30px_0px_#FFC87B]',
                  {
                    'bg-[#FFAB22]': isReached,
                    'bg-[#C1C3C8]': !isReached,
                  },
                )}
              />
            </div>
          );
        })}

        <div
          data-cy={DATA_CY_STEPPED_DISCOUNT_TOOLTIP_TEXT}
          className='absolute top-1 z-30! -translate-y-1/2'
          style={{ left: `${adjustProgressPercent(progressPercent, progress, discounts)}%` }}
        >
          <div
            data-cy={DATA_CY_STEPPED_DISCOUNT_TOOLTIP}
            className='h-[17px] w-1.5 rounded-[20px] bg-white shadow-[0px_4px_4px_0px_#00000040]'
            data-tooltip-id={tooltipId}
            data-tooltip-content={localization.steppedDiscount(
              priceFormatter(data?.needToNextStep),
            )}
          />
          <When condition={progress < discounts[discounts.length - 1].neededAmount}>
            <Tooltip
              isOpen
              id={tooltipId}
              place='top'
              className='-top-[57px]! w-[110px]! border-2! border-white! px-1! py-1! text-center! text-[10px]! leading-3! font-light!'
            />
          </When>
        </div>
      </div>
      <div
        className={clsx(
          amountClassName,
          'mt-2 ml-[78px] flex justify-between text-sm text-gray-600',
        )}
      >
        {discounts.map((item, idx) => (
          <div key={idx} className='flex text-center'>
            <span className='mx-0.5 text-[10px] leading-[26px] font-light text-[#76767C]'>
              {localization.IRCurrency}
            </span>
            <div
              data-cy={DATA_CY_STEPS}
              className='text-sm leading-[26px] font-bold text-[#424244]'
            >
              {item?.neededAmount.toLocaleString('fa-IR')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
